import React, { FC } from 'react';

import { graphql } from 'gatsby';
import { Box, Heading, Text } from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
// @ts-ignore
import useScrollInfo from 'react-element-scroll-hook';
import {
  Drupal_ParagraphFlexiTimelineItem,
  Drupal_ParagraphFlexiTimelineSection,
  Drupal_ParagraphSeoImage
} from 'generated/drupal-graphql';
import { getProcessedValue } from '@lucid/utils';
import { LucidTheme, mqMin } from '@lucid/theme';

interface FlexiTimelineSectionProps {
  entity: Drupal_ParagraphFlexiTimelineSection;
}

interface ItemEntities {
  entity: Drupal_ParagraphFlexiTimelineItem;
}

const FlexiTimelineSection: FC<FlexiTimelineSectionProps> = ({ entity }) => {
  const timelineItemEntities = entity?.fieldTimelineItems as ItemEntities[];
  const timelineItems = timelineItemEntities?.map(({ entity }) => entity);
  const [scrollInfo, setRef, ref] = useScrollInfo();

  const calculateXScroll = (scrollInfo: any, direction: 'left' | 'right') => {
    const increment = 540;
    let xCoordinate = 0;
    const { x } = scrollInfo;

    if (direction === 'left') {
      if (x.value - increment > 0) {
        xCoordinate = x.value - increment;
      }
    } else {
      if (x.value + increment > x.total) {
        xCoordinate = x.total;
      } else {
        xCoordinate = x.value + increment;
      }
    }

    return xCoordinate;
  };

  return (
    <Box
      ref={setRef}
      as="section"
      css={(theme: LucidTheme) => ({
        label: 'flexiTimelineSection',
        display: 'flex',
        flexdirection: 'column',
        flexwrap: 'wrap',
        justifycontent: 'space-between',
        position: 'relative',
        backgroundColor: theme.colors.coolGrey[0],
        width: `calc(100% + ${theme.space[8]}px)`,
        paddingLeft: theme.space[2],
        [mqMin.tablet]: {
          width: '100%',
          backgroundColor: 'initial'
        }
      })}
      gridColumn="1 / -1"
      mb={8}
      mt={2}
      mx={[-5, -5, -5, 0]}
      overflowX={['hidden', 'hidden', 'hidden', 'hidden', 'auto']}
      overflowY="visible"
      pb={8}
      px={[2, 2, 2, 2, 6]}
    >
      <Box
        display="flex"
        flexWrap={['wrap', 'wrap', 'wrap', 'wrap', 'nowrap']}
        mx={[-2, -2, -2, -2, -4]}
        pl={[0, 0, 0, 0, 8]}
        position="relative"
      >
        <Box
          alignItems="center"
          css={{
            pointerEvents: scrollInfo.x.value > 20 ? 'initial' : 'none'
          }}
          display={['none', 'none', 'none', 'none', 'flex']}
          left="-2rem"
          opacity={scrollInfo.x.value > 20 ? '1' : 0}
          position="sticky"
          zIndex={2}
        >
          <Box
            css={{ cursor: 'pointer' }}
            onClick={
              () =>
                ref.current.scroll({
                  left: calculateXScroll(scrollInfo, 'left'),
                  top: 0,
                  behavior: 'smooth'
                })
              // eslint-disable-next-line react/jsx-curly-newline
            }
          >
            <svg
              fill="none"
              height="28"
              viewBox="0 0 36 28"
              width="36"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M35.0039 14.0469H3.00391"
                stroke="#D916A8"
                strokeMiterlimit="10"
                strokeWidth="4"
              />
              <path
                d="M15.6914 2L3.00175 14.0475L15.6914 26.0001"
                stroke="#D916A8"
                strokeMiterlimit="10"
                strokeWidth="4"
              />
            </svg>
          </Box>
        </Box>
        {timelineItems.map(
          ({
            id,
            fieldTimelineHeader,
            fieldTimelineBody,
            fieldTimelineImage,
            fieldTimelineImageWidth,
            fieldImagePxFromBottom,
            fieldImagePxLeft
          }) => {
            const image = fieldTimelineImage?.entity as Drupal_ParagraphSeoImage;
            return (
              <Box
                key={id!}
                as="article"
                css={(theme: LucidTheme) => ({
                  backgroundColor: theme.colors.coolGrey[0],
                  width: `calc(130px + ${theme.space[4]}px)`
                })}
                p={4}
              >
                <Heading
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.blue[60],
                    marginTop: 0
                  })}
                  variant="COsubheadS700"
                >
                  {fieldTimelineHeader}
                </Heading>
                <Text variant="COfinePrintM400">
                  {ReactHtmlParser(getProcessedValue(fieldTimelineBody))}
                </Text>
                {image && (
                  <Box position="relative">
                    <img
                      alt={image.fieldAltText || ''}
                      css={{
                        position: 'relative',
                        marginTop: fieldImagePxFromBottom || 0,
                        left: fieldImagePxLeft || 0,
                        width: fieldTimelineImageWidth || '120px',
                        maxWidth: 'unset'
                      }}
                      loading="lazy"
                      src={image.fieldImageLink || ''}
                    />
                  </Box>
                )}
              </Box>
            );
          }
        )}
        <Box
          alignItems="center"
          css={{
            pointerEvents:
              scrollInfo.x.total - scrollInfo.x.value > 20 ? 'initial' : 'none'
          }}
          display={['none', 'none', 'none', 'none', 'flex']}
          opacity={scrollInfo.x.total - scrollInfo.x.value > 20 ? '1' : 0}
          position="sticky"
          right="-30px"
          zIndex={2}
        >
          <Box
            css={{ cursor: 'pointer' }}
            onClick={
              () =>
                ref.current.scroll({
                  left: calculateXScroll(scrollInfo, 'right'),
                  top: 0,
                  behavior: 'smooth'
                })
              // eslint-disable-next-line react/jsx-curly-newline
            }
          >
            <svg
              fill="none"
              height="28"
              viewBox="0 0 35 28"
              width="35"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 14.0469H32"
                stroke="#D916A8"
                strokeMiterlimit="10"
                strokeWidth="4"
              />
              <path
                d="M19.3125 2L32.0022 14.0475L19.3125 26.0001"
                stroke="#D916A8"
                strokeMiterlimit="10"
                strokeWidth="4"
              />
            </svg>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FlexiTimelineSection;

export const query = graphql`
  fragment flexiTimelineSection on Drupal_ParagraphFlexiTimelineSection {
    fieldTimelineItems {
      entity {
        ... on Drupal_ParagraphFlexiTimelineItem {
          id
          fieldTimelineHeader
          fieldTimelineBody {
            processed
          }
          fieldTimelineImage {
            entity {
              ... on Drupal_ParagraphSeoImage {
                fieldImageLink
                fieldAltText
              }
            }
          }
          fieldTimelineImageWidth
          fieldImagePxFromBottom
          fieldImagePxLeft
        }
      }
    }
  }
`;
